import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  MessageSquare, Send, Bot, User, Loader2, Phone, Mail, 
  ChevronLeft, RefreshCw, MoreVertical, CheckCircle, Clock,
  AlertCircle, Sparkles, UserCheck, X, Filter, Search
} from 'lucide-react';
import AdminLayout from './AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  open: { label: 'Abierta', color: 'bg-blue-500', textColor: 'text-blue-600' },
  pending_ai: { label: 'IA', color: 'bg-yellow-500', textColor: 'text-yellow-600' },
  pending_human: { label: 'Humano', color: 'bg-purple-500', textColor: 'text-purple-600' },
  resolved: { label: 'Resuelta', color: 'bg-green-500', textColor: 'text-green-600' },
  closed: { label: 'Cerrada', color: 'bg-gray-500', textColor: 'text-gray-600' }
};

const INTENT_LABELS = {
  comprar: 'Compra',
  vender: 'Venta',
  rentar: 'Renta',
  legal: 'Legal',
  fiscal: 'Fiscal',
  credito: 'Crédito',
  avaluo: 'Avalúo',
  consulta_general: 'General'
};

export default function AdminOmnichannel() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [useAI, setUseAI] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  const messagesEndRef = useRef(null);
  const token = localStorage.getItem('upim_token');

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    loadConversations();
    // Poll for new messages every 10 seconds
    const interval = setInterval(loadConversations, 10000);
    return () => clearInterval(interval);
  }, [navigate, statusFilter]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversations = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await axios.get(
        `${API_URL}/api/omnichannel/conversations?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConversations(response.data.conversations || []);
      setLoading(false);
    } catch (error) {
      console.error('Error loading conversations:', error);
      setLoading(false);
    }
  };

  const loadConversation = async (convId) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/omnichannel/conversations/${convId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedConversation(response.data);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error loading conversation:', error);
    }
  };

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage.trim() || !selectedConversation || sending) return;

    setSending(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/omnichannel/conversations/${selectedConversation.id}/send`,
        {
          conversation_id: selectedConversation.id,
          content: newMessage,
          use_ai: useAI,
          ai_agent_type: selectedConversation.ai_agent_type || 'atencion_premium'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setNewMessage('');
      // Reload conversation to get updated messages
      await loadConversation(selectedConversation.id);
      loadConversations();
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Error al enviar mensaje');
    } finally {
      setSending(false);
    }
  };

  const updateStatus = async (status) => {
    if (!selectedConversation) return;
    
    try {
      await axios.post(
        `${API_URL}/api/omnichannel/conversations/${selectedConversation.id}/status`,
        { conversation_id: selectedConversation.id, status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadConversation(selectedConversation.id);
      loadConversations();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Ahora';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
  };

  const formatPhone = (phone) => {
    if (!phone) return '';
    // Format Mexican phone: 52 55 1234 5678
    const clean = phone.replace(/\D/g, '');
    if (clean.length === 12) {
      return `+${clean.slice(0, 2)} ${clean.slice(2, 4)} ${clean.slice(4, 8)} ${clean.slice(8)}`;
    }
    return phone;
  };

  const filteredConversations = conversations.filter(conv => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (conv.customer_name?.toLowerCase().includes(term)) ||
      (conv.customer_phone?.includes(term)) ||
      (conv.last_message?.toLowerCase().includes(term))
    );
  });

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="flex h-[calc(100vh-64px)]" data-testid="omnichannel-inbox">
        {/* Conversations List */}
        <div className="w-96 bg-white border-r border-gray-200 flex flex-col">
          {/* Header */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-green-500" />
                Inbox WhatsApp
              </h2>
              <button
                onClick={loadConversations}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <RefreshCw className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            
            {/* Search */}
            <div className="relative mb-3">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar conversación..."
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            
            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="">Todas las conversaciones</option>
              <option value="open">Abiertas</option>
              <option value="pending_ai">Pendiente IA</option>
              <option value="pending_human">Pendiente Humano</option>
              <option value="resolved">Resueltas</option>
            </select>
          </div>
          
          {/* Conversation List */}
          <div className="flex-1 overflow-y-auto">
            {filteredConversations.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No hay conversaciones</p>
              </div>
            ) : (
              filteredConversations.map(conv => (
                <button
                  key={conv.id}
                  onClick={() => loadConversation(conv.id)}
                  className={`w-full p-4 text-left border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                    selectedConversation?.id === conv.id ? 'bg-green-50' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-medium text-gray-900 truncate">
                          {conv.customer_name || formatPhone(conv.customer_phone)}
                        </p>
                        <span className="text-xs text-gray-500">{formatTime(conv.last_message_at)}</span>
                      </div>
                      <p className="text-sm text-gray-500 truncate mb-2">{conv.last_message || 'Sin mensajes'}</p>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_CONFIG[conv.status]?.color} text-white`}>
                          {STATUS_CONFIG[conv.status]?.label}
                        </span>
                        {conv.detected_intent && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                            {INTENT_LABELS[conv.detected_intent] || conv.detected_intent}
                          </span>
                        )}
                        {conv.ai_agent_type && (
                          <Bot className="w-3 h-3 text-yellow-500" title={`Agente: ${conv.ai_agent_type}`} />
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col bg-gray-50">
          {selectedConversation ? (
            <>
              {/* Chat Header */}
              <div className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Phone className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {selectedConversation.customer_name || formatPhone(selectedConversation.customer_phone)}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span>{formatPhone(selectedConversation.customer_phone)}</span>
                      {selectedConversation.detected_state && (
                        <span className="capitalize">• {selectedConversation.detected_state.replace(/_/g, ' ')}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={selectedConversation.status}
                    onChange={(e) => updateStatus(e.target.value)}
                    className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="open">Abierta</option>
                    <option value="pending_ai">Pendiente IA</option>
                    <option value="pending_human">Pendiente Humano</option>
                    <option value="resolved">Resuelta</option>
                    <option value="closed">Cerrada</option>
                  </select>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4">
                <div className="max-w-3xl mx-auto space-y-4">
                  {messages.map((msg, idx) => (
                    <div
                      key={msg.id || idx}
                      className={`flex gap-3 ${msg.direction === 'outbound' ? 'justify-end' : ''}`}
                    >
                      {msg.direction === 'inbound' && (
                        <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="w-4 h-4 text-gray-600" />
                        </div>
                      )}
                      <div
                        className={`max-w-[70%] rounded-2xl px-4 py-2.5 ${
                          msg.direction === 'outbound'
                            ? msg.is_ai_generated
                              ? 'bg-yellow-100 text-gray-800'
                              : 'bg-green-500 text-white'
                            : 'bg-white text-gray-800 shadow-sm'
                        }`}
                      >
                        {msg.direction === 'outbound' && msg.is_ai_generated && (
                          <div className="flex items-center gap-1 text-xs text-yellow-700 mb-1">
                            <Bot className="w-3 h-3" />
                            <span>IA - {msg.ai_agent_type}</span>
                          </div>
                        )}
                        <p className="whitespace-pre-wrap text-sm">{msg.content}</p>
                        <p className={`text-xs mt-1 ${
                          msg.direction === 'outbound' 
                            ? msg.is_ai_generated ? 'text-yellow-600' : 'text-green-200'
                            : 'text-gray-400'
                        }`}>
                          {formatTime(msg.created_at)}
                        </p>
                      </div>
                      {msg.direction === 'outbound' && !msg.is_ai_generated && (
                        <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <UserCheck className="w-4 h-4 text-white" />
                        </div>
                      )}
                      {msg.direction === 'outbound' && msg.is_ai_generated && (
                        <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0">
                          <Bot className="w-4 h-4 text-yellow-800" />
                        </div>
                      )}
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              </div>

              {/* Input */}
              <div className="p-4 bg-white border-t border-gray-200">
                <form onSubmit={sendMessage} className="max-w-3xl mx-auto">
                  <div className="flex items-center gap-2 mb-2">
                    <label className="flex items-center gap-2 text-sm text-gray-600">
                      <input
                        type="checkbox"
                        checked={useAI}
                        onChange={(e) => setUseAI(e.target.checked)}
                        className="rounded border-gray-300 text-yellow-500 focus:ring-yellow-500"
                      />
                      <Bot className="w-4 h-4 text-yellow-500" />
                      Usar IA ({selectedConversation.ai_agent_type || 'atencion_premium'})
                    </label>
                  </div>
                  <div className="flex items-end gap-3">
                    <textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          sendMessage();
                        }
                      }}
                      placeholder={useAI ? 'Escribe un mensaje para que la IA responda...' : 'Escribe tu respuesta...'}
                      className="flex-1 px-4 py-3 border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-green-500"
                      rows={1}
                    />
                    <button
                      type="submit"
                      disabled={!newMessage.trim() || sending}
                      className="p-3 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 text-white rounded-xl transition-colors"
                    >
                      {sending ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Send className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageSquare className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">Selecciona una conversación para ver los mensajes</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
